import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';
import KeycloakService from '../services/KeycloakService';

export interface ReportSessionPayload {
  message: string;
  sync_datasets?: { category: string; id: string; file: string }[];
}

export const ReportsAPI = {
  /**
   * Discover relevant datasets matching a natural-language search query.
   * Calls the REST endpoint directly (not the agent session).
   */
  discoverDatasets: async (
    searchQuery: string,
    datasetType = 'ALL',
  ): Promise<any> =>
    (await initAxios())
      ?.post('/reports/discover', {
        search_query: searchQuery,
        dataset_type: datasetType,
      })
      .then((resp) => ({ success: true, result: resp.data }))
      .catch((err) => ({ success: false, result: parseErrorMsg(err) })),

  /**
   * Opens a Managed Agents streaming session and returns the raw fetch Response
   * so the caller can consume the SSE body as a ReadableStream.
   *
   * Token is refreshed via KeycloakService before the request — same logic as
   * the axios interceptor in axiosHelper.ts — since fetch does not go through axios.
   */
  createSession: async (
    payload: ReportSessionPayload,
    teamId: string,
    signal?: AbortSignal,
  ): Promise<Response> => {
    if (!KeycloakService.isLoggedIn()) {
      KeycloakService.doLogout();
      throw new Error('User is not logged in');
    }

    try {
      await KeycloakService.updateToken();
    } catch (error) {
      if (KeycloakService.isSessionDeadError(error)) {
        console.error(
          'Token refresh failed. Session is dead. Logging out.',
          error,
        );
        KeycloakService.doLogout();
        throw error;
      }

      // A timeout, offline transition, or token-endpoint 5xx is not proof that
      // the refresh token is invalid. Preserve the page and let the heartbeat
      // recover instead of redirecting away from an in-progress report.
      console.warn(
        'Token refresh failed transiently. Retaining session.',
        error,
      );
      throw error;
    }

    const token = KeycloakService.getToken();
    const baseUrl = process.env.REACT_APP_API_URL ?? '';

    return fetch(`${baseUrl}/reports/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Selected-Team-Id': teamId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: signal,
    });
  },
};

export default ReportsAPI;
